import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from '../icons/Icon.js';
import { cx } from '../utils/cx.js';

/**
 * 画面タイトル（title 22/28 明朝 SemiBold）。戻るリンク・説明・右アクション（最大 2 つを推奨）。
 */
export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label?: string };
  /** 画面タイトルの上の小さな分類（様式名など） */
  eyebrow?: ReactNode;
  className?: string;
  /** 大きい見出し（display 28/36） */
  size?: 'title' | 'display';
}

export function PageHeader({ title, description, actions, back, eyebrow, className, size = 'title' }: PageHeaderProps) {
  return (
    <header className={cx('mb-6 flex flex-col gap-3', className)}>
      {back ? (
        <Link to={back.to} className="inline-flex min-tap w-fit items-center gap-1 rounded-input pr-3 text-callout text-fg-secondary hover:bg-sunken">
          <Icon name="arrow_back" size={20} />
          {back.label ?? '戻る'}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* basis 0 だと、操作ボタンが広いときに見出しが 1 文字ずつ折れて縦に伸びる（スマホ幅の会議一覧で発生）。16rem を下回るなら操作を次の行に送る */}
        <div className="min-w-0 [flex:1_1_16rem]">
          {eyebrow ? <p className="mb-1 text-caption text-fg-tertiary">{eyebrow}</p> : null}
          <h1 className={cx('font-serif wrap-name', size === 'display' ? 'text-display' : 'text-title')}>{title}</h1>
          {description ? <p className="mt-1 text-callout text-fg-secondary">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

export function SectionTitle({ children, className, actions }: { children: ReactNode; className?: string; actions?: ReactNode }) {
  return (
    <div className={cx('mb-3 flex items-center justify-between gap-3', className)}>
      <h2 className="text-headline">{children}</h2>
      {actions}
    </div>
  );
}
